import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CsIconModule } from '@components/icon';
import { QuizChatMessage } from '@services/quiz-chat-service';
import { QuizEngine, QuizPhotoPair } from '@services/consultation-quiz-engine-service/consultation-quiz-engine.types';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { UserService } from '@services/user';
import { ConnectionService } from '@services/connection-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { AppConfig } from 'client/app/app.config';

@Component({
  selector: 'app-quiz-photo-upload',
  templateUrl: './quiz-photo-upload.html',
  standalone: true,
  imports: [NgTemplateOutlet, CsIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** Photo card for a capture question. See client/components/quiz/CLAUDE.md. */
export class QuizPhotoUploadComponent {
  readonly engine = input.required<QuizEngine>();
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly userService = inject(UserService);
  private readonly connectionService = inject(ConnectionService);
  private readonly commonUtilityHelper = inject(CommonUtilityHelper);
  private readonly appConfig = inject(AppConfig);
  readonly scanFrameImage: string = '/assets/images/complete-profile/scan-frame.svg';
  readonly dashedBorderImage: string = '/assets/images/quiz-photo-dashed-border.svg';
  readonly dashedBorderCardImage: string = '/assets/images/quiz-photo-dashed-border-card.svg';
  readonly checkIcon: string = 'circle_tick_filled';

  /** Tapping the dashed capture card runs the same instant-checkup action as the footer CTA. */
  readonly capture = output<void>();
  /** Tapping the already-uploaded preview opens that photo's instant-checkup detail (emits its id). */
  readonly openInstantCheckup = output<string>();

  private readonly currentPhotoState = signal<string>('');
  private readonly currentCheckupId = signal<string>('');
  private currentCanonicalUrl = '';
  private readonly alternatePhotoState = signal<string>('');
  // Instant-checkup id of the alternate photo (from the API path); '' when only a URL is known.
  private readonly alternateCheckupId = signal<string>('');
  // Canonical (non-signed) URL of the alternate photo, used to resolve the checkup id lazily on tap.
  private alternateCanonicalUrl = '';

  private readonly currentCheckupType = computed(() => {
    const option = this.engine().photoCtaOption();
    if (!option) return '';
    try {
      return (JSON.parse(option.params?.queryParams || '{}') as { tag?: string }).tag || '';
    } catch {
      return '';
    }
  });
  private readonly alternateCheckupType = computed(
    () => this.instantCheckupService.getAlternatePhotoType(this.currentCheckupType()),
  );

  readonly imageUrl: Signal<string> = computed(() => {
    if (!this.engine().photoCtaOption()) return '';
    const checkupType = this.currentCheckupType();
    if (!checkupType) return '';
    return this.instantCheckupService.getPhotoIllustrationUrl(checkupType, this.userService.getUserGender());
  });
  readonly currentLabel: Signal<string> = computed(() => this.publishedPair()?.current?.label
    || this.instantCheckupService.getPhotoTypeLabel(this.currentCheckupType()));
  readonly alternateLabel: Signal<string> = computed(() => this.publishedPair()?.alternate?.label
    || this.instantCheckupService.getPhotoTypeLabel(this.alternateCheckupType()));
  private readonly publishedPair: Signal<QuizPhotoPair | null> = computed(() => this.engine().photoPair?.() ?? null);
  readonly alternatePhotoUrl: Signal<string> = computed(
    () => this.publishedPair()?.alternate?.url ?? this.alternatePhotoState(),
  );
  readonly currentPhotoUrl: Signal<string> = computed(
    () => this.publishedPair()?.current?.url ?? this.currentPhotoState(),
  );
  private readonly alternateIsAskedFirst: Signal<boolean> = computed(() => {
    const order = this.engine().questionOrder?.() || [];
    const current = order.indexOf(this.instantCheckupService.getPhotoQuestionId(this.currentCheckupType()));
    const alternate = order.indexOf(this.instantCheckupService.getPhotoQuestionId(this.alternateCheckupType()));
    if (current < 0 || alternate < 0) return true;
    return alternate < current;
  });

  readonly isTwoCard: Signal<boolean> = computed(() => {
    const published = this.publishedPair();
    if (published) return published.showPair;
    return !!this.alternatePhotoState() && this.alternateIsAskedFirst();
  });

  constructor() {
    effect(() => {
      if (this.publishedPair()) return;
      const alternateType = this.alternateCheckupType();
      const messages = (this.engine().messages?.() || []) as QuizChatMessage[];
      void this.resolveAlternatePhoto(alternateType, messages);
    });
    effect(() => {
      if (this.publishedPair()) return;
      const currentType = this.currentCheckupType();
      const messages = (this.engine().messages?.() || []) as QuizChatMessage[];
      void this.resolveCurrentPhoto(currentType, messages);
    });
  }

  /** Show the pair's other photo if the user already uploaded it — chat history first, API fallback. */
  private async resolveAlternatePhoto(alternateType: string, messages: QuizChatMessage[]): Promise<void> {
    this.clearAlternate();
    if (!alternateType) return;
    const alternateQuestionId = this.instantCheckupService.getPhotoQuestionId(alternateType);
    const uploaded = messages.find(
      (message) => message.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER
        && message.get('questionId') === alternateQuestionId,
    );
    // `Message` holds the canonical (non-loadable) GCS path; the signed, display-ready URL is on
    // the temp* fields — same fallback chain the chatV2 image-card uses.
    const fromChat = uploaded && (uploaded.get('tempCompressedImage') || uploaded.get('tempThumbnailUrl')
      || uploaded.get('tempUrl') || uploaded.get('Message'));
    if (await this.isDisplayableImage(fromChat)) {
      this.alternatePhotoState.set(fromChat);
      // No checkup id in the chat row — keep the canonical URL to resolve it on tap.
      this.alternateCanonicalUrl = (uploaded.get('Message') as string) || fromChat;
      return;
    }
    const checkup = await this.instantCheckupService.getRecentInstantCheckupOfType(
      this.connectionService.getActingUser(), alternateType,
    );
    const apiUrl = checkup?.imagePath || '';
    // A deleted photo (URL present but the file is 404) collapses back to the single dashed card.
    if (await this.isDisplayableImage(apiUrl)) {
      this.alternatePhotoState.set(apiUrl);
      this.alternateCheckupId.set(checkup?.objectId || '');
      this.alternateCanonicalUrl = apiUrl;
    }
  }

  private async resolveCurrentPhoto(currentType: string, messages: QuizChatMessage[]): Promise<void> {
    this.currentPhotoState.set('');
    this.currentCheckupId.set('');
    this.currentCanonicalUrl = '';
    if (!currentType) return;
    const { canonicalUrl, checkupId, url } = await this.findUploadedPhoto(currentType, messages);
    if (!await this.isDisplayableImage(url)) return;
    this.currentPhotoState.set(url);
    this.currentCheckupId.set(checkupId);
    this.currentCanonicalUrl = canonicalUrl;
  }

  /** Chat history first, API fallback — the frontend-driven engine has no chat rows to search. */
  private async findUploadedPhoto(
    checkupType: string, messages: QuizChatMessage[],
  ): Promise<{ canonicalUrl: string, checkupId: string, url: string }> {
    const questionId = this.instantCheckupService.getPhotoQuestionId(checkupType);
    const uploaded = messages.find(
      (message) => message.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER
        && message.get('questionId') === questionId,
    );
    const fromChat = uploaded && (uploaded.get('tempCompressedImage') || uploaded.get('tempThumbnailUrl')
      || uploaded.get('tempUrl') || uploaded.get('Message'));
    if (fromChat) {
      return {
        canonicalUrl: (uploaded.get('Message') as string) || (fromChat as string),
        checkupId: '',
        url: fromChat as string,
      };
    }
    const checkup = await this.instantCheckupService.getRecentInstantCheckupOfType(
      this.connectionService.getActingUser(), checkupType,
    );
    const url = checkup?.imagePath || '';
    return { canonicalUrl: url, checkupId: checkup?.objectId || '', url };
  }

  private clearAlternate(): void {
    this.alternatePhotoState.set('');
    this.alternateCheckupId.set('');
    this.alternateCanonicalUrl = '';
  }

  private async isDisplayableImage(url: string): Promise<boolean> {
    if (!url) return false;
    return !(await this.commonUtilityHelper.checkImageDeleted(url));
  }

  onCapture(): void {
    const published = this.publishedPair()?.current;
    if (published) {
      void this.openCheckup(published.checkupId, published.canonicalUrl);
      return;
    }
    if (this.currentPhotoState()) {
      void this.openCheckup(this.currentCheckupId(), this.currentCanonicalUrl);
      return;
    }
    this.capture.emit();
  }

  /** Keyboard activation for the div-as-button capture card (Enter/Space). */
  onCaptureKeydown(event: Event): void {
    event.preventDefault();
    this.onCapture();
  }

  async onOpenAlternate(): Promise<void> {
    const published = this.publishedPair()?.alternate;
    if (published) {
      await this.openCheckup(published.checkupId, published.canonicalUrl);
      return;
    }
    await this.openCheckup(this.alternateCheckupId(), this.alternateCanonicalUrl);
  }

  private async openCheckup(knownId: string, canonicalUrl: string): Promise<void> {
    let checkupId = knownId;
    if (!checkupId && canonicalUrl) {
      const checkup = await this.connectionService.fetchInstantCheckupByImageUrl(
        this.connectionService.getActingUser(), canonicalUrl,
      );
      checkupId = checkup?.id || '';
    }
    if (checkupId) this.openInstantCheckup.emit(checkupId);
  }

  onOpenAlternateKeydown(event: Event): void {
    event.preventDefault();
    void this.onOpenAlternate();
  }
}
